import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { stringify } from 'query-string';
import React, { useEffect, useState } from 'react';
import { Image, ImageOff, LayoutGrid, List } from 'lucide-react';
import { useHistory } from 'react-router-dom';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import { getThemes } from '@dashboard/hooks/queries/useThemeQueries';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import CheckoutListItem from '@dashboard/components/CheckoutListItem';
import EmptyState from '@dashboard/components/EmptyState';
import EventListItem from '@dashboard/components/EventListItem';
import FilterInput from '@dashboard/components/Form/components/FilterInput';
import SearchInput from '@dashboard/components/Form/components/SearchInput';
import FormListItem from '@dashboard/components/FormListItem';
import PageListSkeleton from '@dashboard/components/PageListSkeleton';
import Spinner from '@dashboard/components/Spinner';
import { sellerPagesKeyWithFilter, themesKey } from '@/dashboard/lib/queryKeys';
import storage from '@dashboard/services/storage';

const FILTER_OPTIONS = {
  PUBLISHED: {
    value: 'published',
    label: 'Active',
  },
  ARCHIVED: {
    value: 'archived',
    label: 'Archived',
  },
};

function getStatusFromQuery(status) {
  const statusOption = Object.keys(FILTER_OPTIONS).find(
    key => FILTER_OPTIONS[key].value === status,
  );
  return statusOption || Object.keys(FILTER_OPTIONS)[0];
}

const LAYOUT_KEY = 'pages-list-layout';
const IMAGES_KEY = 'pages-list-images';

function readStored(key, fallback) {
  try {
    return storage.local.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

/** A list view choice remembered in this browser, separately for checkout pages, events and forms. */
function useStoredChoice(key, fallback) {
  const [value, setValueState] = useState(() => readStored(key, fallback));
  const setValue = next => {
    setValueState(next);
    try {
      storage.local.setItem(key, next);
    } catch {
      // storage can be unavailable; the choice then lasts until the next load
    }
  };
  return [value, setValue];
}

const LAYOUT_BUTTON_CLASS =
  'relative inline-flex size-7 items-center justify-center rounded-md pointer-coarse:after:absolute pointer-coarse:after:-inset-y-2 pointer-coarse:after:-inset-x-0.5 text-muted-foreground transition-colors hover:text-foreground aria-pressed:bg-gray-100 dark:aria-pressed:bg-accent aria-pressed:text-foreground [&>svg]:size-4';

function ImagesToggle({ showImages, onChange }) {
  const label = showImages ? 'Hide images' : 'Show images';
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={!showImages}
      onClick={() => onChange(!showImages)}
      className="relative inline-flex size-8 shrink-0 items-center pointer-coarse:after:absolute pointer-coarse:after:-inset-1.5 justify-center rounded-lg border border-gray-200 dark:border-border text-muted-foreground transition-colors hover:text-foreground aria-pressed:bg-gray-100 dark:aria-pressed:bg-accent aria-pressed:text-foreground [&>svg]:size-4"
    >
      {showImages ? <Image /> : <ImageOff />}
    </button>
  );
}

function LayoutToggle({ layout, onChange }) {
  return (
    <div
      role="group"
      aria-label="View"
      className="inline-flex shrink-0 items-center gap-0.5 rounded-lg border border-gray-200 dark:border-border p-0.5"
    >
      <button
        type="button"
        aria-label="List view"
        title="List view"
        aria-pressed={layout === 'list'}
        onClick={() => onChange('list')}
        className={LAYOUT_BUTTON_CLASS}
      >
        <List />
      </button>
      <button
        type="button"
        aria-label="Grid view"
        title="Grid view"
        aria-pressed={layout === 'grid'}
        onClick={() => onChange('grid')}
        className={LAYOUT_BUTTON_CLASS}
      >
        <LayoutGrid />
      </button>
    </div>
  );
}

const LIST_ITEM_BY_TYPE = {
  checkout: CheckoutListItem,
  event: EventListItem,
  form: FormListItem,
};

export default function PagesList({
  type,
  emptyState,
  createHref,
  searchPlaceholder = 'Search',
  leftControls = null,
  extraFilter,
}) {
  const query = useUrlQuery();
  const history = useHistory();
  const { sellerId, token } = useAuth();
  const { getPages } = usePageQueries();

  const [search, setSearch] = useState(query.search || '');
  const [status, setStatus] = useState(getStatusFromQuery(query.status));
  const [layout, setLayout] = useStoredChoice(`${LAYOUT_KEY}:${type}`, 'list');
  const [images, setImages] = useStoredChoice(`${IMAGES_KEY}:${type}`, 'show');

  useQuery({
    queryKey: themesKey(sellerId),
    queryFn: () => getThemes(token, sellerId),
    enabled: !!token && !!sellerId,
  });

  useEffect(() => {
    setSearch(query.search || '');
  }, [query.search]);

  useEffect(() => {
    setStatus(getStatusFromQuery(query.status));
  }, [query.status]);

  const filter = {
    type: [type],
    status: FILTER_OPTIONS[status].value,
    search,
    ...extraFilter,
  };

  useEffect(() => {
    history.replace({
      search: stringify({
        ...query,
        ...(status && { status: FILTER_OPTIONS[status].value }),
        ...(search && { search }),
      }),
    });
  }, [status, search]);

  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading, hasNextPage } =
    useInfiniteQuery({
      queryKey: sellerPagesKeyWithFilter(sellerId, filter),
      queryFn: async ({ pageParam }) =>
        await getPages(sellerId, {
          ...filter,
          sort: JSON.stringify({ createdAt: -1 }),
          limit: 10,
          skip: pageParam * 10,
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.has_more) return undefined;
        return allPages.length;
      },
    });

  const ListItem = LIST_ITEM_BY_TYPE[type];

  return (
    <>
      <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2 items-center">{leftControls}</div>

        <div className="flex flex-wrap items-center gap-2">
          <SearchInput
            className="max-sm:basis-full"
            onChange={setSearch}
            initialValue={search}
            name={`search_${type}_pages`}
            placeholder={searchPlaceholder}
          />
          <FilterInput options={FILTER_OPTIONS} onChange={setStatus} value={status} />
          <ImagesToggle
            showImages={images !== 'hide'}
            onChange={show => setImages(show ? 'show' : 'hide')}
          />
          <LayoutToggle layout={layout === 'grid' ? 'grid' : 'list'} onChange={setLayout} />
        </div>
      </div>

      <ListBody
        type={type}
        data={data}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        filter={filter}
        emptyState={emptyState}
        createHref={createHref}
        ListItem={ListItem}
        layout={layout}
        showImages={images !== 'hide'}
      />
    </>
  );
}

function ListBody({
  type,
  data,
  isLoading,
  isError,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  filter,
  emptyState,
  createHref,
  ListItem,
  layout,
  showImages,
}) {
  if (isLoading) {
    return (
      <div className="mb-20 rounded-md border border-gray-200 dark:border-border overflow-hidden">
        <PageListSkeleton />
      </div>
    );
  }

  if (isError) {
    return <div className="py-3">Failed to show pages. Please refresh or contact support.</div>;
  }

  const hasPages = data?.pages?.[0]?.data?.length > 0;

  if (!hasPages && filter.search) {
    return (
      <div className="mb-20 rounded-md border border-gray-200 dark:border-border overflow-hidden">
        <EmptyState>
          <EmptyState.Title>{emptyState.searchTitle}</EmptyState.Title>
          <EmptyState.Subtitle>Try searching for something else.</EmptyState.Subtitle>
        </EmptyState>
      </div>
    );
  }

  if (!hasPages && filter.status !== FILTER_OPTIONS.PUBLISHED.value) {
    return (
      <div className="mb-20 rounded-md border border-gray-200 dark:border-border overflow-hidden">
        <EmptyState>
          <EmptyState.Title>{emptyState.filteredTitle}</EmptyState.Title>
          <EmptyState.Subtitle>Adjust filters to see more.</EmptyState.Subtitle>
        </EmptyState>
      </div>
    );
  }

  if (!hasPages) {
    return (
      <div className="mb-20 rounded-md border border-gray-200 dark:border-border overflow-hidden">
        <EmptyState>
          <EmptyState.Title>{emptyState.defaultTitle}</EmptyState.Title>
          <EmptyState.Subtitle>{emptyState.defaultSubtitle}</EmptyState.Subtitle>
          <EmptyState.Action to={createHref}>{emptyState.createLabel}</EmptyState.Action>
        </EmptyState>
      </div>
    );
  }

  const isGrid = layout === 'grid';

  return (
    <div
      data-pages-layout={layout}
      data-pages-images={showImages ? undefined : 'hidden'}
      className={
        isGrid
          ? 'mb-20'
          : 'mb-20 rounded-md border border-gray-200 dark:border-border overflow-hidden'
      }
    >
      <BottomScrollListener
        className={
          isGrid
            ? 'grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4'
            : '[&>*:nth-last-child(2)]:border-b-0'
        }
        onBottom={() => {
          if (!hasNextPage) return;
          fetchNextPage();
        }}
      >
        {data.pages.map(dataPage =>
          dataPage.data.map(page => (
            <ListItem
              key={page._id}
              page={page}
              isMainView
              isGrid={isGrid}
              {...(type === 'form' ? { showAbandonment: true } : {})}
            />
          )),
        )}
      </BottomScrollListener>

      {isFetchingNextPage && <Spinner className="m-4" />}
    </div>
  );
}
